/**
 * AdministrationPage — the Admin/Configuration V2 surface (FULL-0159 / PROD-UX-073,
 * MASTER_FLOW §14C.5 "Admin/Configuration", §3 GUI-managed Skill Pool, §5 provider onboarding).
 *
 * One read-only index over the five administration domains the product already ships:
 * providers & models, Skills, tools, connections, and the authorization/audit evidence behind
 * them. It does not replace the existing detail surfaces and it is deliberately NOT a second
 * policy engine:
 *
 *   - every value comes from an existing, already-permission-gated GET (the provider records,
 *     provisioning contract, connections health, Tool Registry, connector metadata, entitlement
 *     readiness, Skill candidate list, Skill leaderboard and the governed Skill-domain contract);
 *   - this page mounts NO mutation. Enabling a provider stays on the two-person approval path,
 *     Skill assignment stays on the per-employee audited surface, tool testing stays on
 *     /connections, and the audit trail stays on /activity-log;
 *   - disabled / unavailable / pending_review / approval_required are classified by
 *     `adminConfigurationModel` from the server's own fields, so they cannot collapse into one
 *     another on this screen;
 *   - an unloaded or unreadable source is `unknown`; it never renders as zero, "off" or healthy.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  agentConnectionsApi,
  agentRunsApi,
  agentsApi,
  connectionsApi,
  skillManagementApi,
  skillsApi,
  techScoutApi,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import RoleSurface from "../components/designSystem/RoleSurface";
import { classifySource } from "../components/statusVocabulary";
import { operationTypeLabel } from "../components/operationTypeVocabulary";
import { riskGradeLabel } from "../components/riskGradeVocabulary";
import {
  adminDisplayText,
  adminInteger,
  adminStateMeta,
  aiKeySourceLabel,
  classifyAiConfiguration,
  classifyConnection,
  classifyConnectorDefinition,
  classifyEmployeeEnablement,
  classifyProviderRecord,
  classifySkillCandidate,
  classifyToolRecord,
  providerHealthMeta,
  providerRecordEvidence,
  toolAuthorization,
} from "../components/adminConfigurationModel";

const obj = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : null);
const arr = (value) => (Array.isArray(value) ? value : null);

const CREDENTIAL_STATE_LABELS = {
  reference_recorded: "הפניה לסוד נרשמה (הערך לא נשמר בפרויקציה)",
};

const ACTIVATION_STATE_LABELS = {
  provisioned_operator_gated: "הוקצה — הפעלה מותנית במפעיל",
  enabled_by_operator_approval: "הופעל באישור מפעיל",
};

const CONNECTION_LABELS = {
  openclaw: "OpenClaw Runtime",
  teamviewer: "TeamViewer",
  ms365: "Microsoft 365 / Graph",
};

// ─── Shared presentation ────────────────────────────────────────────────────

export function AdminStateChip({ state, testId }) {
  const meta = adminStateMeta(state);
  return (
    <span
      className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${meta.cls}`}
      data-admin-state={meta.key}
      data-testid={testId}
    >
      {meta.label}
    </span>
  );
}

export function AdminSourceNote({ id, query, label = null }) {
  const source = classifySource(query);
  const at = source.since ? new Date(source.since).toLocaleString("he-IL") : null;
  const text =
    source.key === "fresh"
      ? `נטען מהשרת ${at}`
      : source.key === "stale"
      ? `הרענון נכשל — מוצג עותק מ-${at}, שאינו עדכני`
      : source.key === "failed"
      ? "המקור לא נטען — אין כאן נתונים מומצאים"
      : "טוען מהשרת…";
  return (
    <p
      className="text-[11px] text-gray-500"
      data-testid={`admin-source-${id}`}
      data-source-state={source.key}
    >
      {label ? `${label}: ` : ""}
      {text}
    </p>
  );
}

function Section({ id, title, description, children }) {
  return (
    <section
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4"
      data-testid={`admin-section-${id}`}
      aria-labelledby={`admin-heading-${id}`}
    >
      <div>
        <h2 id={`admin-heading-${id}`} className="font-semibold text-gray-800 text-base">
          {title}
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      {children}
    </section>
  );
}

function EvidenceLine({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-500 shrink-0 w-40">{label}:</span>
      <span className="text-gray-700 break-words">{value}</span>
    </div>
  );
}

function UnloadedNotice({ id, query, permissionGated = false }) {
  if (query.data != null) return null;
  const status = query.error?.response?.status;
  if (permissionGated && status === 403) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 space-y-1" data-testid={`admin-unloaded-${id}`}>
        <AdminStateChip state="no_permission" testId={`admin-state-${id}`} />
        <p className="text-xs text-gray-600">אין הרשאה לקרוא את הראיה הזו. ההרשאה נקבעת בשרת.</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 space-y-1" data-testid={`admin-unloaded-${id}`}>
      <AdminStateChip state="unknown" testId={`admin-state-${id}`} />
      <p className="text-xs text-gray-600">המקור לא נטען או שאינו קריא — אין הצגת מצב מומצא.</p>
    </div>
  );
}

// ─── Providers and models ───────────────────────────────────────────────────

function ProviderRow({ record, index }) {
  const source = obj(record) || {};
  const state = classifyProviderRecord(record);
  const evidence = providerRecordEvidence(record);
  const name =
    adminDisplayText(source.display_name) || adminDisplayText(source.provider_id) || "ספק ללא שם";
  const testId = `admin-provider-${index}`;

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-gray-800">{name}</div>
          <div className="text-[11px] text-gray-500 font-mono break-all">
            {adminDisplayText(source.provider_id) || "—"}
          </div>
        </div>
        <AdminStateChip state={state.state} testId={`${testId}-state`} />
      </div>

      <p className="text-xs text-gray-600">
        {state.state === "approval_required"
          ? "הרשומה קיימת אך אינה מופעלת. ההפעלה מותנית באישור של אדם נוסף — זו אינה השבתה ואינה תקלה."
          : state.state === "enabled"
          ? "הרשומה הופעלה על ידי מפעיל מורשה. הפעלה אינה מבטיחה שהניתוב החי צורך אותה."
          : "מצב הרשומה לא דווח."}
      </p>

      <div className="space-y-1">
        <EvidenceLine
          label="מצב הקצאה"
          value={ACTIVATION_STATE_LABELS[evidence.activationState] || "לא דווח"}
        />
        <EvidenceLine
          label="מצב הפניה"
          value={CREDENTIAL_STATE_LABELS[evidence.credentialState] || "לא דווח"}
        />
        <EvidenceLine label="סכימת הפניה" value={evidence.credentialScheme} />
        <EvidenceLine
          label="אורך שם ההפניה"
          value={evidence.credentialReferenceLength == null ? null : String(evidence.credentialReferenceLength)}
        />
        <EvidenceLine label="טביעת ההפניה" value={evidence.credentialReferenceDigest} />
        <EvidenceLine
          label="ערך הסוד"
          value={evidence.credentialValueReturned === false ? "לא הוחזר" : "מצב ההחזרה לא דווח"}
        />
        <EvidenceLine
          label="הקצאת סודות"
          value={evidence.secretsProvisioned === false ? "לא בוצעה בנתיב הזה" : null}
        />
        <EvidenceLine
          label="בדיקת חיבור"
          value={
            evidence.connectionVerified === false
              ? "לא בוצעה בנתיב הזה"
              : evidence.connectionVerified === true
              ? "נבדק"
              : null
          }
        />
        <EvidenceLine
          label="שינוי ניתוב"
          value={evidence.routeChanged === false ? "לא בוצע" : evidence.routeChanged === true ? "בוצע" : null}
        />
        <EvidenceLine
          label="הענקת הרשאה"
          value={
            evidence.authorityGranted === false
              ? "לא הוענקה הרשאה"
              : evidence.authorityGranted === true
              ? "הוענקה הרשאה"
              : null
          }
        />
        <EvidenceLine
          label="אירוע ביקורת"
          value={
            evidence.auditWritten === true
              ? "נכתב אירוע ביקורת"
              : evidence.auditWritten === false
              ? "לא דווחה כתיבת אירוע ביקורת"
              : null
          }
        />
      </div>

      {evidence.liveRoutingConsumesRecord === false && (
        <p className="text-[11px] text-amber-700" data-testid={`${testId}-routing-caveat`}>
          הניתוב החי אינו צורך את הרשומה. הפעלת הרשומה אינה פעולת ניתוב.
        </p>
      )}
    </div>
  );
}

function ProvidersSection({ healthQuery, providersQuery, contractQuery, providerHealthQuery }) {
  const providers = arr(providersQuery.data?.providers);
  const contract = obj(contractQuery.data);
  const ai = obj(healthQuery.data?.ai_analysis);
  const aiState = classifyAiConfiguration(ai);
  const providerHealth = obj(providerHealthQuery.data);
  const summaries = arr(providerHealth?.summaries);

  return (
    <Section
      id="providers"
      title="ספקים ומודלים"
      description="ספקים שנרשמו, ברירת המחדל של ניתוח ה-AI ועדויות הבריאות שנמדדו. אין כאן הפעלה, אישור או שינוי ניתוב."
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">ספקים שנרשמו</h3>
          <AdminSourceNote id="providers" query={providersQuery} label="רשימת הספקים" />
          {providersQuery.data != null && providers && providers.length === 0 && (
            <p className="text-xs text-gray-500" data-testid="admin-providers-empty">
              לא נרשמו ספקים בנתיב ההקמה. היעדר רשומות אינו ראיה שהספקים אינם מוגדרים במערכת.
            </p>
          )}
          {providers && providers.map((record, index) => (
            <ProviderRow key={index} record={record} index={index} />
          ))}
          <UnloadedNotice id="providers" query={providersQuery} />
        </div>

        <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="admin-provider-contract">
          <h3 className="text-sm font-semibold text-gray-700">גבול ההפעלה</h3>
          <AdminSourceNote id="provider-contract" query={contractQuery} label="חוזה ההקמה" />
          {contract ? (
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {[
                ["two_person_rule", "אישור של שני אנשים שונים"],
                ["approvals_single_use", "אישור חד-פעמי"],
                ["strict_binding", "קישור מדויק לפעולה ולטיוטה"],
                ["crisis_mode_gated", "כפוף למצב חירום"],
              ].map(([key, label]) => (
                <span
                  key={key}
                  data-testid={`admin-contract-${key}`}
                  className={`px-1.5 py-0.5 rounded-full border ${
                    contract[key] ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-gray-100 text-gray-500 border-gray-300"
                  }`}
                >
                  {label}: {contract[key] ? "כן" : "לא דווח"}
                </span>
              ))}
              <span
                data-testid="admin-contract-no-secrets"
                className="px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
              >
                מקצה סודות: {contract.secrets_provisioned_by_this_path === false ? "לא" : "לא דווח"}
              </span>
              <span
                data-testid="admin-contract-no-connection-test"
                className="px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
              >
                בודק חיבור: {contract.connection_tested_by_this_path === false ? "לא" : "לא דווח"}
              </span>
              <span
                data-testid="admin-contract-routing"
                className="px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
              >
                הניתוב החי צורך רשומה: {contract.live_routing_consumes_record === true ? "כן" : "לא"}
              </span>
            </div>
          ) : (
            <p className="text-xs text-gray-600">חוזה ההקמה לא נטען — אין כאן הצהרת הרשאה.</p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="admin-default-model">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-700">ברירת המחדל של ניתוח AI</h3>
            <AdminStateChip state={aiState.state} testId="admin-ai-state" />
          </div>
          <AdminSourceNote id="ai" query={healthQuery} label="מצב החיבורים" />
          {ai ? (
            <div className="space-y-1">
              <EvidenceLine label="ספק" value={adminDisplayText(ai.provider)} />
              <EvidenceLine label="מודל" value={adminDisplayText(ai.model)} />
              <EvidenceLine label="מקור מפתח Anthropic" value={aiKeySourceLabel(ai.anthropic_key_source)} />
              <EvidenceLine label="מקור מפתח OpenAI" value={aiKeySourceLabel(ai.openai_key_source)} />
              <p className="text-xs text-gray-600">
                {aiState.state === "requires_configuration"
                  ? "ספק או מודל טרם הוגדרו."
                  : aiState.state === "unavailable"
                  ? "ספק ומודל מוגדרים, אך אין מפתח ספק שמיש — הניתוב לא יפעל."
                  : aiState.state === "enabled"
                  ? "ספק, מודל ומפתח שמיש מוגדרים."
                  : "מצב ברירת המחדל לא דווח."}
              </p>
            </div>
          ) : (
            <UnloadedNotice id="ai" query={healthQuery} />
          )}
        </div>

        <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="admin-provider-health">
          <h3 className="text-sm font-semibold text-gray-700">עדויות בריאות ספקים שנמדדו</h3>
          <AdminSourceNote id="provider-health" query={providerHealthQuery} label="קריאות ספק" />
          {providerHealthQuery.isError ? (
            <UnloadedNotice id="provider-health" query={providerHealthQuery} permissionGated />
          ) : summaries ? (
            summaries.length === 0 ? (
              <p className="text-xs text-gray-500">
                לא נרשמו קריאות ספק בחלון 30 הימים. היעדר קריאות אינו מעיד שהספק בריא.
              </p>
            ) : (
              <div className="space-y-1">
                {summaries.map((summary, index) => {
                  const source = obj(summary) || {};
                  const health = providerHealthMeta(source.health);
                  const calls = adminInteger(source.calls, { min: 0 });
                  return (
                    <div
                      key={index}
                      className="flex flex-wrap items-center justify-between gap-2 text-xs border-b border-gray-100 pb-1"
                      data-testid={`admin-provider-health-${index}`}
                    >
                      <span className="text-gray-700">
                        {adminDisplayText(source.provider) || "—"} · {adminDisplayText(source.model) || "—"}
                      </span>
                      <span className="text-gray-500">
                        קריאות: {calls == null ? "לא דווח" : calls}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-full border ${health.cls}`}>{health.label}</span>
                    </div>
                  );
                })}
              </div>
            )
          ) : null}
        </div>
      </div>
    </Section>
  );
}

// ─── Skills ─────────────────────────────────────────────────────────────────

function SkillsSection({ candidatesQuery, leaderboardQuery, skillContractQuery }) {
  const candidates = arr(candidatesQuery.data);
  const reports = arr(leaderboardQuery.data);
  const contract = obj(skillContractQuery.data);
  const domains = arr(contract?.domains);

  return (
    <Section
      id="skills"
      title="מיומנויות"
      description="מועמדים שממתינים לסקירה, עובדים וזמינותם, וקטלוג התחומים הממשליים. סקירה או הקצאה כאן אינן מעניקות כלי, הרשאה או חיבור."
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">מועמדים שממתינים לסקירה</h3>
          <AdminSourceNote id="skill-candidates" query={candidatesQuery} label="מועמדים חיצוניים" />
          {candidatesQuery.data != null && candidates && candidates.length === 0 && (
            <p className="text-xs text-gray-500" data-testid="admin-skill-candidates-empty">
              אין מועמדים שממתינים לסקירה כרגע. היעדר מועמדים אינו ראיה שכל המיומנויות אומתו.
            </p>
          )}
          {candidates && candidates.map((candidate, index) => {
            const source = obj(candidate) || {};
            const state = classifySkillCandidate(candidate);
            return (
              <div
                key={index}
                className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-lg p-2"
                data-testid={`admin-skill-candidate-${index}`}
              >
                <div>
                  <div className="text-xs font-medium text-gray-800">
                    {adminDisplayText(source.technology_name) || "מועמד ללא שם"}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {adminDisplayText(source.category) || "ללא קטגוריה"} · ציון תשומת לב:{" "}
                    {adminInteger(source.attention_score, { min: 0 }) ?? "לא דווח"}
                  </div>
                </div>
                <AdminStateChip state={state.state} testId={`admin-skill-candidate-${index}-state`} />
              </div>
            );
          })}
          <UnloadedNotice id="skill-candidates" query={candidatesQuery} />
          <p className="text-[11px] text-gray-500">
            מסלול הסקירה בלבד: <Link className="text-blue-700 underline" to="/tech-scout">מרכז החדשנות</Link>.
            סקירה אינה הרשאה ואינה התקנה.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">עובדים וזמינות</h3>
          <AdminSourceNote id="skill-employees" query={leaderboardQuery} label="דוח עובדים" />
          {reports && reports.length === 0 && (
            <p className="text-xs text-gray-500">לא נמצאו עובדים בדוח.</p>
          )}
          {reports && reports.map((report, index) => {
            const source = obj(report) || {};
            const state = classifyEmployeeEnablement(report);
            const agentId = adminDisplayText(source.agent_id);
            return (
              <div
                key={index}
                className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-lg p-2"
                data-testid={`admin-skill-employee-${index}`}
              >
                <div>
                  <div className="text-xs font-medium text-gray-800">
                    {adminDisplayText(source.agent_name) || "עובד ללא שם"}
                  </div>
                  <div className="text-[11px] text-gray-500" data-testid={`admin-skill-employee-${index}-evidence`}>
                    {source.has_outcome_evidence === true
                      ? `עדות תוצאה: ציון ${adminInteger(source.overall_score) ?? "לא דווח"}`
                      : "אין עדות תוצאה — אין להסיק ביצועים."}
                  </div>
                </div>
                <AdminStateChip state={state.state} testId={`admin-skill-employee-${index}-state`} />
                {agentId && (
                  <Link
                    className="text-[11px] text-blue-700 underline"
                    to={`/agents/${encodeURIComponent(agentId)}/skills`}
                  >
                    מיומנויות העובד
                  </Link>
                )}
              </div>
            );
          })}
          <UnloadedNotice id="skill-employees" query={leaderboardQuery} />
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">תחומים ממשליים</h3>
          <AdminSourceNote id="skill-domains" query={skillContractQuery} label="חוזה המיומנויות" />
          {domains ? (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="admin-skill-domains">
                {domains.map((entry, index) => (
                  <span
                    key={index}
                    className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
                  >
                    {adminDisplayText(entry.domain) || "—"}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">
                הקצאת מיומנות מתחילה ברמה 0 ובגרסה 1 ואינה מעניקה כלי, הרשאה או חיבור. ההקצאה מתבצעת
                במשטח העובד המבוקר, לא כאן.
              </p>
              <p className="text-[11px] text-gray-500">
                <Link className="text-blue-700 underline" to="/workforce">בחירת עובד להקצאה</Link>
              </p>
            </>
          ) : (
            <UnloadedNotice id="skill-domains" query={skillContractQuery} />
          )}
        </div>
      </div>
    </Section>
  );
}

// ─── Tools ──────────────────────────────────────────────────────────────────

function ToolsSection({ healthQuery, toolsQuery, entitlementQuery }) {
  const healthTools = arr(healthQuery.data?.tool_registry);
  const registry = arr(toolsQuery.data);
  const entitlement = obj(entitlementQuery.data);
  const enforcement = entitlement?.enforcement_currently_enabled;
  const agents = arr(entitlement?.agents);
  const blockedAgents = agents
    ? agents.filter((entry) => (arr(obj(entry)?.blocked_write_actions) || []).length > 0)
    : null;

  return (
    <Section
      id="tools"
      title="כלים"
      description="מצב תפעולי של כלי הפיילוט, רישום ההרשאות של כל כלי ומצב אכיפת הזכאות. אין כאן הפעלה או שינוי הרשאה."
    >
      <div className="space-y-3">
        <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="admin-tool-enforcement">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-700">אכיפת זכאות כלים</h3>
            <AdminStateChip
              state={enforcement === true ? "enabled" : enforcement === false ? "disabled" : "unknown"}
              testId="admin-tool-enforcement-state"
            />
          </div>
          <AdminSourceNote id="tool-entitlement" query={entitlementQuery} label="מוכנות זכאות" />
          <p className="text-xs text-gray-600">
            {enforcement === false
              ? "האכיפה מושבתת בהגדרת השרת. אין כאן בקרה שמשנה את הדגל; שינוי כזה הוא החלטת מפעיל נפרדת."
              : enforcement === true
              ? "האכיפה מופעלת. כלים שזכאות העובד אינה מאפשרת להם ייחסמו לפני ביצוע."
              : "מצב האכיפה לא דווח."}
          </p>
          {blockedAgents && (
            <p className="text-xs text-gray-600" data-testid="admin-tool-blocked">
              עובדים עם פעולות כתיבה חסומות: {blockedAgents.length}
              {blockedAgents.length > 0
                ? ` (${blockedAgents
                    .map((entry) => adminDisplayText(obj(entry)?.agent_name) || "עובד ללא שם")
                    .join(", ")})`
                : ""}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">מצב תפעולי של כלי הפיילוט</h3>
          <AdminSourceNote id="tool-health" query={healthQuery} label="מצב החיבורים" />
          {healthTools && healthTools.length === 0 && (
            <p className="text-xs text-gray-500">לא דווחו כלי פיילוט.</p>
          )}
          {healthTools && healthTools.map((tool, index) => {
            const source = obj(tool) || {};
            const state = classifyToolRecord(tool);
            const auth = toolAuthorization(tool);
            return (
              <div
                key={index}
                className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-lg p-2"
                data-testid={`admin-tool-health-${index}`}
              >
                <div>
                  <div className="text-xs font-mono text-gray-800">{adminDisplayText(source.id) || "—"}</div>
                  <div className="text-[11px] text-gray-500">
                    {adminDisplayText(source.description) || "ללא תיאור"}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    הרשאה: {auth.label}
                    {adminDisplayText(source.readiness_dependency)
                      ? ` · תלות: ${adminDisplayText(source.readiness_dependency)}`
                      : ""}
                  </div>
                </div>
                <AdminStateChip state={state.state} testId={`admin-tool-health-${index}-state`} />
              </div>
            );
          })}
          <UnloadedNotice id="tool-health" query={healthQuery} />
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">רישום כלים והרשאות</h3>
          <AdminSourceNote id="tool-registry" query={toolsQuery} label="Tool Registry" />
          {registry ? (
            registry.length === 0 ? (
              <p className="text-xs text-gray-500">הרישום ריק.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-separate border-spacing-y-1">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-right font-medium px-2 py-1">כלי</th>
                      <th className="text-right font-medium px-2 py-1">פעולה</th>
                      <th className="text-right font-medium px-2 py-1">סיכון</th>
                      <th className="text-right font-medium px-2 py-1">אישור</th>
                      <th className="text-right font-medium px-2 py-1">Audit</th>
                      <th className="text-right font-medium px-2 py-1">Tiers / Roles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registry.map((tool, index) => {
                      const source = obj(tool) || {};
                      const auth = toolAuthorization(tool);
                      const tiers = arr(source.allowed_tiers) || [];
                      const roles = arr(source.allowed_roles) || [];
                      return (
                        <tr key={index} className="bg-gray-50" data-testid={`admin-tool-registry-${index}`}>
                          <td className="px-2 py-2 font-mono text-gray-800 rounded-r-lg">
                            {adminDisplayText(source.name) || "—"}
                          </td>
                          <td className="px-2 py-2 text-gray-600">{operationTypeLabel(source.operation_type)}</td>
                          <td className="px-2 py-2 text-gray-600">{riskGradeLabel(source.risk_level)}</td>
                          <td className="px-2 py-2 text-gray-600">{auth.label}</td>
                          <td className="px-2 py-2 text-gray-600">
                            {auth.audit === true ? "נדרש" : auth.audit === false ? "לא נדרש" : "לא ידוע"}
                          </td>
                          <td className="px-2 py-2 text-gray-600 rounded-l-lg">
                            {tiers.length || roles.length
                              ? `${tiers.length} tiers · ${roles.length} roles`
                              : "לא דווח"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <UnloadedNotice id="tool-registry" query={toolsQuery} />
          )}
          <p className="text-[11px] text-gray-500">
            בדיקת חיבור לכלי מתבצעת במשטח <Link className="text-blue-700 underline" to="/connections">החיבורים</Link>.
          </p>
        </div>
      </div>
    </Section>
  );
}

// ─── Connections ────────────────────────────────────────────────────────────

function ConnectionRow({ kind, payload, index }) {
  const state = classifyConnection(kind, payload);
  const detail =
    kind === "openclaw"
      ? adminDisplayText(payload?.mode_label)
      : kind === "teamviewer"
      ? aiKeySourceLabel(payload?.credential_source)
      : adminDisplayText(payload?.mailbox);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-lg p-2"
      data-testid={`admin-connection-${index}`}
    >
      <div>
        <div className="text-xs font-medium text-gray-800">{CONNECTION_LABELS[kind]}</div>
        <div className="text-[11px] text-gray-500">
          {detail || "לא דווח"}
          {state.reason === "mock_adapter_no_real_connection" ? " · מתאם Mock — אין חיבור אמיתי" : ""}
          {state.reason === "invalid_secret_reference" ? " · הפנית הסוד אינה תקינה" : ""}
        </div>
      </div>
      <AdminStateChip state={state.state} testId={`admin-connection-${index}-state`} />
    </div>
  );
}

function ConnectionsSection({ healthQuery, connectorsQuery }) {
  const health = obj(healthQuery.data);
  const browser = obj(health?.browser_worker);
  const connectors = arr(connectorsQuery.data);

  return (
    <Section
      id="connections"
      title="חיבורים"
      description="מצב החיבורים החיצוניים וסוגי החיבורים הנתמכים. מצב מוגדר אינו בדיקת חיבור חיה; בדיקה כזו נשארת במשטח החיבורים."
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">חיבורים מוגדרים</h3>
          <AdminSourceNote id="connections" query={healthQuery} label="מצב החיבורים" />
          {health ? (
            ["openclaw", "teamviewer", "ms365"].map((kind, index) => (
              <ConnectionRow key={kind} kind={kind} payload={health[kind]} index={index} />
            ))
          ) : (
            <UnloadedNotice id="connections" query={healthQuery} />
          )}
          {browser && (
            <div className="border border-gray-200 rounded-lg p-2 space-y-1" data-testid="admin-browser-worker">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-medium text-gray-800">עבודת דפדפן מפורשת</div>
                  <div className="text-[11px] text-gray-500">
                    {adminDisplayText(browser.detail) || "פירוט לא דווח"}
                  </div>
                </div>
                <AdminStateChip
                  state={classifyConnection("browser_worker", browser).state}
                  testId="admin-browser-worker-state"
                />
              </div>
              <p className="text-[11px] text-gray-600">
                הפעלה דורשת אישור: {browser.approval_required === true ? "כן" : "לא דווח"}
                {adminDisplayText(browser.blocked_reason)
                  ? ` · סיבת חסימה: ${adminDisplayText(browser.blocked_reason)}`
                  : ""}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">סוגי חיבורים</h3>
          <AdminSourceNote id="connector-types" query={connectorsQuery} label="קטלוג מחברים" />
          {connectors ? (
            connectors.length === 0 ? (
              <p className="text-xs text-gray-500">לא נרשמו סוגי חיבורים.</p>
            ) : (
              connectors.map((definition, index) => {
                const source = obj(definition) || {};
                const state = classifyConnectorDefinition(definition);
                return (
                  <div
                    key={index}
                    className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-lg p-2"
                    data-testid={`admin-connector-${index}`}
                  >
                    <div>
                      <div className="text-xs font-medium text-gray-800">
                        {adminDisplayText(source.display_name) || adminDisplayText(source.connector_id) || "—"}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {adminDisplayText(source.category) || "ללא קטגוריה"} ·{" "}
                        {adminDisplayText(source.scope) || "ללא היקף"} · קריאה בלבד:{" "}
                        {source.read_only === true ? "כן" : source.read_only === false ? "לא" : "לא דווח"} ·
                        בדיקת חיבור: {source.supports_test === true ? "נתמכת" : "לא נתמכת"}
                      </div>
                    </div>
                    <AdminStateChip state={state.state} testId={`admin-connector-${index}-state`} />
                  </div>
                );
              })
            )
          ) : (
            <UnloadedNotice id="connector-types" query={connectorsQuery} />
          )}
          <p className="text-[11px] text-gray-500">
            פרטי חיבור ובדיקות: <Link className="text-blue-700 underline" to="/connections">חיבורים ורישום כלים</Link> ·{" "}
            <Link className="text-blue-700 underline" to="/platform-health">בריאות פלטפורמה</Link>.
          </p>
        </div>
      </div>
    </Section>
  );
}

// ─── Authorization and audit ────────────────────────────────────────────────

function AuthorizationSection({ contractQuery, toolsQuery, entitlementQuery }) {
  const contract = obj(contractQuery.data);
  const registry = arr(toolsQuery.data);
  const entitlement = obj(entitlementQuery.data);
  const approvalCount = registry
    ? registry.filter((tool) => toolAuthorization(tool).key === "approval_required").length
    : null;
  const strongCount = registry
    ? registry.filter((tool) => toolAuthorization(tool).key === "strong_approval_required").length
    : null;
  const auditCount = registry
    ? registry.filter((tool) => obj(tool)?.audit_required === true).length
    : null;
  const enforcement = entitlement?.enforcement_currently_enabled;

  return (
    <Section
      id="authorization"
      title="הרשאה וביקורת"
      description="מה שנקבע בשרת ומה שנכתב לביקורת. משטח זה אינו מבצע פעולה, אינו מאשר ואינו משנה מדיניות או דגל."
    >
      <div className="space-y-3 text-xs text-gray-700">
        <div className="flex flex-wrap gap-1.5">
          <span className="px-1.5 py-0.5 rounded-full border bg-blue-50 text-blue-800 border-blue-200">
            הפעלת ספק: {contract?.two_person_rule === "approver_must_differ_from_executor" ? "שני אנשים שונים" : "לא דווח"}
          </span>
          <span className="px-1.5 py-0.5 rounded-full border bg-blue-50 text-blue-800 border-blue-200">
            אישור חד-פעמי: {contract?.approvals_single_use === true ? "כן" : "לא דווח"}
          </span>
          <span className="px-1.5 py-0.5 rounded-full border bg-blue-50 text-blue-800 border-blue-200">
            כפוף למצב חירום: {contract?.crisis_mode_gated === true ? "כן" : "לא דווח"}
          </span>
          <span className="px-1.5 py-0.5 rounded-full border bg-gray-100 text-gray-700 border-gray-300">
            אכיפת זכאות: {enforcement === true ? "מופעלת" : enforcement === false ? "מושבתת" : "לא דווחה"}
          </span>
        </div>

        <div className="space-y-1">
          <div>
            כלים הדורשים אישור ברישום: {approvalCount == null ? "לא נטען" : approvalCount}
          </div>
          <div>
            כלים הדורשים אישור כפול: {strongCount == null ? "לא נטען" : strongCount}
          </div>
          <div>
            כלים עם חובת Audit: {auditCount == null ? "לא נטען" : auditCount}
          </div>
        </div>

        <p>
          שינויי ספק נכתבים לאירוע ביקורת בשרת. רשומות ספק מציגות{" "}
          <span className="font-medium">אירוע ביקורת: נכתב</span> רק כאשר השדה{" "}
          <span className="font-mono">audit_written</span> הוא true; אחרת נאמר שלא דווחה כתיבה.
        </p>

        <div className="flex flex-wrap gap-2">
          <Link className="text-blue-700 underline" to="/activity-log">יומן פעילות</Link>
          <Link className="text-blue-700 underline" to="/agent-permissions">הרשאות AI</Link>
          <Link className="text-blue-700 underline" to="/connections">חיבורים ורישום כלים</Link>
        </div>

        <p className="text-[11px] text-gray-500">
          אין במשטח הזה כפתור אישור, הפעלה, שמירה או בדיקה. פעולות מתבצעות רק בחוזי ה-API הקיימים
          ובמשטחים המבוקרים שלהם, והשרת מתעד אותן.
        </p>
      </div>
    </Section>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AdministrationPage() {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const healthQuery = useQuery({
    queryKey: ["administration-connections-health"],
    queryFn: connectionsApi.health,
    enabled: isAdmin,
    retry: false,
  });
  const providersQuery = useQuery({
    queryKey: ["administration-providers"],
    queryFn: connectionsApi.providerOnboardingProviders,
    enabled: isAdmin,
    retry: false,
  });
  const contractQuery = useQuery({
    queryKey: ["administration-provider-contract"],
    queryFn: connectionsApi.providerOnboardingProvisioningContract,
    enabled: isAdmin,
    retry: false,
  });
  const providerHealthQuery = useQuery({
    queryKey: ["administration-provider-health"],
    queryFn: () => agentRunsApi.providerHealth({ since_days: 30 }),
    enabled: isAdmin,
    retry: false,
  });
  const candidatesQuery = useQuery({
    queryKey: ["administration-skill-candidates"],
    queryFn: techScoutApi.attention,
    enabled: isAdmin,
    retry: false,
  });
  const leaderboardQuery = useQuery({
    queryKey: ["administration-skill-employees"],
    queryFn: () => skillsApi.leaderboard(),
    enabled: isAdmin,
    retry: false,
  });
  const skillContractQuery = useQuery({
    queryKey: ["administration-skill-contract"],
    queryFn: skillManagementApi.contract,
    enabled: isAdmin,
    retry: false,
  });
  const toolsQuery = useQuery({
    queryKey: ["administration-tools"],
    queryFn: agentsApi.listTools,
    enabled: isAdmin,
    retry: false,
  });
  const entitlementQuery = useQuery({
    queryKey: ["administration-entitlement"],
    queryFn: agentsApi.entitlementReadiness,
    enabled: isAdmin,
    retry: false,
  });
  const connectorsQuery = useQuery({
    queryKey: ["administration-connector-types"],
    queryFn: agentConnectionsApi.connectorTypes,
    enabled: isAdmin,
    retry: false,
  });

  if (!isAdmin) {
    return (
      <div dir="rtl" role="alert" className="p-8 text-center text-gray-600" data-testid="administration-denied">
        אין לך הרשאה לצפות במשטח הניהול וההגדרות.
      </div>
    );
  }

  return (
    <RoleSurface
      role="admin"
      dir="rtl"
      width="standard"
      padding="page"
      title="ניהול והגדרות"
      description="משטח תצוגה מאוחד לספקים, מודלים, מיומנויות, כלים וחיבורים. כל הערך נקרא ממקורות השרת הקיימים; אין כאן פעולה, אישור או הענקת הרשאה."
      testId="administration-role-surface"
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="administration-boundary">
          <p className="font-medium">
            מצבים נפרדים: מושבת, לא זמין, ממתין לסקירה ודורש אישור אינם מתאחדים למצב אחד.
          </p>
          <p className="mt-1">
            המשטח קורא בלבד. הפעלת ספק נשארת במסלול אישור של שני אנשים, הקצאת מיומנות נשארת במשטח
            העובד המבוקר, ובדיקת חיבור נשארת במשטח החיבורים — כולם קיימים ומתועדים בשרת.
          </p>
        </div>

        <ProvidersSection
          healthQuery={healthQuery}
          providersQuery={providersQuery}
          contractQuery={contractQuery}
          providerHealthQuery={providerHealthQuery}
        />
        <SkillsSection
          candidatesQuery={candidatesQuery}
          leaderboardQuery={leaderboardQuery}
          skillContractQuery={skillContractQuery}
        />
        <ToolsSection
          healthQuery={healthQuery}
          toolsQuery={toolsQuery}
          entitlementQuery={entitlementQuery}
        />
        <ConnectionsSection healthQuery={healthQuery} connectorsQuery={connectorsQuery} />
        <AuthorizationSection
          contractQuery={contractQuery}
          toolsQuery={toolsQuery}
          entitlementQuery={entitlementQuery}
        />
      </div>
    </RoleSurface>
  );
}
